import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const NAV_LEFT = [{ id: "calculadora", label: "Simulador" }];

const NAV_RIGHT = [
    { id: "hipotecas", label: "Hipotecas" },
    { id: "proceso", label: "Proceso" },
    { id: "quien-esta-detras", label: "¿Quién soy?" },
    { id: "opiniones", label: "Opiniones" },
];

const HIPOTECAS_MENU = [
    { label: "Hipoteca 100%", slug: "hipoteca-100" },
    { label: "Hipoteca 95%", slug: "hipoteca-95" },
    { label: "Hipoteca 90%", slug: "hipoteca-90" },
    { label: "Hipoteca joven", slug: "hipoteca-joven" },
    { label: "Hipoteca Premium", slug: "hipoteca-premium" },
    { label: "Sin vinculaciones", slug: "hipoteca-sin-vinculaciones" },
    { label: "80% Segunda Viv/Inv", slug: "hipoteca-segunda-vivienda" },
    { label: "Capital privado", slug: "capital-privado" },
    { label: "Reunificación de deudas", slug: "reunificacion-deudas" },
    { label: "¿Necesitas liquidez?", slug: "necesitas-liquidez", path: "/necesitas-liquidez" },
];

function HipotecasDropdown({ onGo }) {
    return (
        <div className="relative group">
            <button
                data-testid="nav-link-hipotecas"
                onClick={() => onGo("hipotecas")}
                className="flex items-center gap-1.5 text-sm font-medium text-white/75 hover:text-white transition-colors duration-200"
            >
                Hipotecas
                <ChevronDown
                    size={14}
                    className="transition-transform duration-200 group-hover:rotate-180"
                />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-[opacity,transform,visibility] duration-200">
                <div
                    data-testid="nav-hipotecas-dropdown"
                    className="w-64 rounded-2xl bg-white shadow-2xl shadow-black/30 border border-slate-100 py-2 overflow-hidden"
                >
                    {HIPOTECAS_MENU.map((h) => (
                        <Link
                            key={h.slug}
                            data-testid={`nav-dd-${h.slug}`}
                            to={h.path || `/hipotecas/${h.slug}`}
                            className="block px-5 py-2.5 text-sm text-slate-700 hover:text-emerald-700 hover:bg-emerald-50/70 border-b border-slate-100 last:border-0 transition-colors"
                        >
                            {h.label}
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [hipotecasOpen, setHipotecasOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToId(id);
    };

    return (
        <header
            data-testid="main-navbar"
            className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${
                scrolled
                    ? "bg-[#112338]/90 backdrop-blur-xl shadow-lg shadow-black/20"
                    : "bg-[#112338]/40 backdrop-blur-md"
            } border-b border-white/10`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
                <button
                    data-testid="nav-logo"
                    onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
                    className="flex items-center"
                    aria-label="Keypoteca inicio"
                >
                    <img
                        src="/assets/logo-white-trim.png"
                        alt="Keypoteca"
                        className="h-8 sm:h-9 w-auto"
                    />
                </button>

                <nav className="hidden lg:flex flex-1 items-center justify-between pl-14 pr-10">
                    <div className="flex items-center">
                        {NAV_LEFT.map((l) => (
                            <button
                                key={l.id}
                                data-testid={`nav-link-${l.id}`}
                                onClick={() => go(l.id)}
                                className="text-sm font-medium text-white/75 hover:text-white transition-colors duration-200"
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-8">
                        {NAV_RIGHT.map((l) =>
                            l.id === "hipotecas" ? (
                                <HipotecasDropdown key={l.id} onGo={go} />
                            ) : (
                                <button
                                    key={l.id}
                                    data-testid={`nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="text-sm font-medium text-white/75 hover:text-white transition-colors duration-200"
                                >
                                    {l.label}
                                </button>
                            )
                        )}
                    </div>
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        data-testid="nav-cta-button"
                        onClick={() => go("contacto")}
                        className="hidden sm:inline-flex items-center rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-5 py-2.5 transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
                    >
                        Estudiar mi caso
                    </button>
                    <button
                        data-testid="nav-menu-toggle"
                        className="lg:hidden text-white p-2"
                        onClick={() => setOpen(!open)}
                        aria-label="Abrir menú"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:hidden overflow-hidden bg-[#112338]/95 backdrop-blur-xl border-t border-white/10"
                    >
                        <div className="px-6 py-6 flex flex-col gap-4">
                            {[...NAV_LEFT, ...NAV_RIGHT].map((l) =>
                                l.id === "hipotecas" ? (
                                    <div key={l.id}>
                                        <button
                                            data-testid="nav-mobile-link-hipotecas"
                                            onClick={() =>
                                                setHipotecasOpen(!hipotecasOpen)
                                            }
                                            className="w-full flex items-center justify-between text-left text-white/85 text-lg font-medium"
                                        >
                                            {l.label}
                                            <ChevronDown
                                                size={18}
                                                className={`transition-transform duration-200 ${
                                                    hipotecasOpen
                                                        ? "rotate-180"
                                                        : ""
                                                }`}
                                            />
                                        </button>
                                        <AnimatePresence>
                                            {hipotecasOpen && (
                                                <motion.div
                                                    data-testid="nav-mobile-hipotecas-list"
                                                    initial={{
                                                        opacity: 0,
                                                        height: 0,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        height: "auto",
                                                    }}
                                                    exit={{
                                                        opacity: 0,
                                                        height: 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.25,
                                                    }}
                                                    className="overflow-hidden"
                                                >
                                                    <div className="pt-2 pb-1 flex flex-col">
                                                        {HIPOTECAS_MENU.map(
                                                            (h) => (
                                                                <Link
                                                                    key={h.slug}
                                                                    data-testid={`nav-mobile-dd-${h.slug}`}
                                                                    to={
                                                                        h.path ||
                                                                        `/hipotecas/${h.slug}`
                                                                    }
                                                                    onClick={() =>
                                                                        setOpen(
                                                                            false
                                                                        )
                                                                    }
                                                                    className="py-2.5 pl-4 text-base text-white/70 hover:text-emerald-300 border-l border-white/10 transition-colors"
                                                                >
                                                                    {h.label}
                                                                </Link>
                                                            )
                                                        )}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                ) : (
                                    <button
                                        key={l.id}
                                        data-testid={`nav-mobile-link-${l.id}`}
                                        onClick={() => go(l.id)}
                                        className="text-left text-white/85 text-lg font-medium"
                                    >
                                        {l.label}
                                    </button>
                                )
                            )}
                            <button
                                data-testid="nav-mobile-cta"
                                onClick={() => go("contacto")}
                                className="mt-2 rounded-full bg-emerald-500 text-white font-semibold px-5 py-3 text-center"
                            >
                                Estudiar mi caso
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
